import React, { useEffect, useState } from 'react';
import Select, { ValueType } from 'react-select';
import EdiText from 'react-editext';
import Dropzone from 'react-dropzone-uploader';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Add01Icon,
  CloudUploadIcon,
  Copy01Icon,
  Delete02Icon,
  File01Icon,
  Tick02Icon,
  UserGroupIcon
} from '@hugeicons/core-free-icons';
import { Loader } from '../../../../../shared/components/core-ui/loader/loader.component';
import { GuestUiModel } from '../../../../../shared/ui-models/guest.ui-model';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { GuestDocumentType } from '../../../../../shared/models/guest.model';
import { PUBLIC_PATH } from '../../../../../shared/config/routes-config';
import { hasAccess } from '../../../../../shared/services/helpers/auth-helper/auth-helper.service';
import { PERMISSIONS_LIST } from '../../../../../shared/config/permissions.config';
import { GUEST_VISIT_TYPES } from '../../reservation-edit.helper';
import COUNTRIES from '../add-guest-modal/countries.json';
import { ErrorInfo, isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import {
  getOneGuest,
  updateGuest,
  deleteGuest,
  uploadGuestDocument,
  updateOneReservations
} from '../../../../shared/services/data.service';
import { birdToast } from '../../../../../shared/components/core-ui/toast';
import Tooltip from '../../../../../ui/Tooltip';

export interface GuestSectionProps {
  data: {
    reservation: ReservationDetailsUiModel;
    leadBooker?: {
      name?: string;
      phoneNumber?: string;
      email?: string;
    };
  };
  eventHandlers: {
    onAddGuest: () => void;
    onGetOneReservation: (id: string) => void;
  };
}

const DOCUMENT_GROUPS = [
  {
    type: GuestDocumentType.NATIONAL_ID,
    title: 'National ID',
    upload: 'Upload identification document',
    matches: (type?: GuestDocumentType) => !type || type === GuestDocumentType.NATIONAL_ID
  },
  {
    type: GuestDocumentType.PASSPORT,
    title: 'Passport',
    upload: 'Upload passport',
    matches: (type?: GuestDocumentType) => type === GuestDocumentType.PASSPORT
  },
  {
    type: GuestDocumentType.MARRIAGE_DOC,
    title: 'Marriage certificate',
    upload: 'Upload marriage certificate',
    matches: (type?: GuestDocumentType) => type === GuestDocumentType.MARRIAGE_DOC
  }
];

const selectStyles = {
  control: (base: any) => ({ ...base, minHeight: '40px', borderRadius: '10px', fontSize: '15px' }),
  menu: (base: any) => ({ ...base, zIndex: 9999, fontSize: '15px' })
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="min-w-0">
    <div className="text-[13px] text-neutral-300 mb-[4px]">{label}</div>
    <div className="text-[15px] text-neutral-500 min-w-0 [&_input]:text-[15px]">{children}</div>
  </div>
);

const initials = (name?: string) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

export const GuestSection: React.FC<GuestSectionProps> = ({
  data: { reservation, leadBooker },
  eventHandlers: { onAddGuest, onGetOneReservation }
}) => {
  const [guest, setGuest] = useState<GuestUiModel | undefined>(undefined);
  const [activeGuestId, setActiveGuestId] = useState<string | undefined>(undefined);
  const [showUpdateLoader, setShowUpdateLoader] = useState(false);
  const [uploadingType, setUploadingType] = useState<GuestDocumentType | undefined>(undefined);
  const [isLoadingDeleteGuest, setIsLoadingDeleteGuest] = useState(false);
  const [copiedField, setCopiedField] = useState<'email' | 'phone' | undefined>(undefined);

  const canModify = hasAccess([
    PERMISSIONS_LIST.reservationsFullAccess,
    PERMISSIONS_LIST.reservationsModifyAccess
  ]);
  const canSeeDocuments = hasAccess([
    PERMISSIONS_LIST.reservationsFullAccess,
    PERMISSIONS_LIST.reservationsModifyAccess,
    PERMISSIONS_LIST.reservationsReadAccess
  ]);
  const guestIds = reservation.guestIds || [];

  useEffect(() => {
    if (reservation?.guestIds && reservation.guestIds.length) {
      setActiveGuestId(reservation.guestIds[0]);
      getOneGuestObject(reservation.guestIds[0]);
    } else {
      setActiveGuestId(undefined);
      setGuest(undefined);
    }
  }, [reservation?.guestIds]);

  function getOneGuestObject(id: string) {
    getOneGuest(id).then((response: GuestUiModel | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        setGuest(response);
      }
    });
  }

  function updateOneGuest(guestData: GuestUiModel) {
    setShowUpdateLoader(true);
    return updateGuest(guestData).then((response: GuestUiModel | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        setGuest(response);
        setShowUpdateLoader(false);
      }
    });
  }

  function deleteOneGuest(guestId: string) {
    setIsLoadingDeleteGuest(true);
    return deleteGuest(guestId)
      .then((response: GuestUiModel | ErrorInfo) => {
        if (!isErrorInfo(response)) {
          setGuest(response);
          return updateOneReservations({
            ...reservation,
            guestIds: reservation?.guestIds.filter((id) => guest?.id !== id)
          }).then(() => onGetOneReservation(reservation?.id || ''));
        }
      })
      .finally(() => setIsLoadingDeleteGuest(false));
  }

  function handleUploadDocument(
    file: File,
    guestId: string,
    reservationId: string,
    type: GuestDocumentType
  ) {
    uploadGuestDocument(file, guestId, reservationId, type).then(() => {
      onGetOneReservation(reservationId);
      setUploadingType(undefined);
    });
  }

  const copyLeadBooker = (field: 'email' | 'phone', value?: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(undefined), 2000);
  };

  const editableText = (value: string | undefined, onSave: (val: string) => void) =>
    canModify ? (
      <EdiText type="text" value={value || ''} onSave={onSave} />
    ) : (
      <span className="block truncate">{value || '—'}</span>
    );

  return (
    <>
      <div className="flex items-center justify-between gap-[12px]">
        <h2 className="m-0 text-[clamp(18px,1.5vw,22px)] font-gilroy-bold text-neutral-500">
          Guests
        </h2>
        {(canModify || guestIds.length === 0) && (
          <button
            type="button"
            onClick={onAddGuest}
            className="inline-flex items-center gap-[6px] px-[16px] py-[10px] rounded-[8px] bg-neutral-500 text-neutral-0 text-[14px] font-gilroy-medium hover:bg-neutral-700 cursor-pointer"
          >
            <HugeiconsIcon icon={Add01Icon} size={16} />
            Add guest
          </button>
        )}
      </div>

      {(leadBooker?.name || leadBooker?.phoneNumber || leadBooker?.email) && (
        <div className="mt-[20px] flex items-center gap-[12px] rounded-[12px] border border-gray-200 px-[16px] py-[12px]">
          <div className="size-[40px] shrink-0 rounded-full bg-primary-600 text-neutral-0 grid place-items-center text-[13px] font-gilroy-bold">
            {initials(leadBooker.name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[12px] text-neutral-300">Booked by</div>
            <div className="text-[15px] font-gilroy-medium text-neutral-500 truncate">
              {leadBooker.name || '—'}
            </div>
          </div>
          <div className="flex items-center gap-[8px] flex-wrap justify-end min-w-0">
            {leadBooker.phoneNumber && (
              <Tooltip title={copiedField === 'phone' ? 'Copied!' : 'Copy phone'}>
                <button
                  type="button"
                  onClick={() => copyLeadBooker('phone', leadBooker.phoneNumber)}
                  className="inline-flex items-center gap-[8px] rounded-[10px] bg-creams-100 px-[12px] py-[8px] text-[14px] text-neutral-500 whitespace-nowrap hover:bg-creams-200 cursor-pointer"
                >
                  {leadBooker.phoneNumber}
                  <HugeiconsIcon
                    icon={copiedField === 'phone' ? Tick02Icon : Copy01Icon}
                    size={16}
                    className="shrink-0 text-neutral-300"
                  />
                </button>
              </Tooltip>
            )}
            {leadBooker.email && (
              <Tooltip title={copiedField === 'email' ? 'Copied!' : 'Copy email'}>
                <button
                  type="button"
                  onClick={() => copyLeadBooker('email', leadBooker.email)}
                  className="inline-flex items-center gap-[8px] max-w-[320px] rounded-[10px] bg-creams-100 px-[12px] py-[8px] text-[14px] text-neutral-500 hover:bg-creams-200 cursor-pointer"
                >
                  <span className="truncate">{leadBooker.email}</span>
                  <HugeiconsIcon
                    icon={copiedField === 'email' ? Tick02Icon : Copy01Icon}
                    size={16}
                    className="shrink-0 text-neutral-300"
                  />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      )}

      {guestIds.length === 0 ? (
        <div className="mt-[20px] rounded-[16px] bg-creams-100 px-[24px] py-[32px] flex flex-col items-center text-center">
          <HugeiconsIcon icon={UserGroupIcon} size={32} className="text-neutral-300" />
          <div className="mt-[12px] text-[16px] font-gilroy-medium text-neutral-500">
            No guests registered yet
          </div>
        </div>
      ) : (
        <>
          {guestIds.length > 1 && (
            <div className="mt-[20px] flex gap-[8px] overflow-x-auto">
              {guestIds.map((guestId, index) => (
                <button
                  key={guestId}
                  type="button"
                  onClick={() => {
                    setActiveGuestId(guestId);
                    getOneGuestObject(guestId);
                  }}
                  className={`shrink-0 px-[16px] py-[8px] rounded-full border text-[14px] font-gilroy-medium cursor-pointer ${
                    activeGuestId === guestId
                      ? 'bg-neutral-500 border-neutral-500 text-neutral-0'
                      : 'border-gray-200 text-neutral-500 hover:border-neutral-500'
                  }`}
                >
                  Guest {index + 1}
                </button>
              ))}
            </div>
          )}

          {showUpdateLoader && (
            <div className="mt-[24px] flex justify-center">
              <Loader />
            </div>
          )}

          {guest && !showUpdateLoader && (
            <div className="mt-[20px]">
              <div className="flex items-center gap-[12px]">
                <div className="size-[44px] shrink-0 rounded-full bg-creams-100 text-neutral-500 grid place-items-center text-[14px] font-gilroy-bold">
                  {initials(`${guest.firstName || ''} ${guest.lastName || ''}`)}
                </div>
                <div className="text-[16px] font-gilroy-medium text-neutral-500 truncate">
                  {[guest.firstName, guest.lastName].filter(Boolean).join(' ') || 'Guest'}
                </div>
              </div>

              <div className="mt-[20px] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-[24px] gap-y-[20px]">
                <Field label="First name">
                  {editableText(guest.firstName, (val) =>
                    updateOneGuest({ ...guest, firstName: val })
                  )}
                </Field>
                <Field label="Last name">
                  {editableText(guest.lastName, (val) =>
                    updateOneGuest({ ...guest, lastName: val })
                  )}
                </Field>
                <Field label="Phone">
                  {editableText(guest.phoneNumber ? String(guest.phoneNumber) : undefined, (val) =>
                    updateOneGuest({ ...guest, phoneNumber: val })
                  )}
                </Field>
                <Field label="Email">
                  {editableText(guest.email, (val) => updateOneGuest({ ...guest, email: val }))}
                </Field>
                <Field label="Nationality">
                  {canModify ? (
                    <Select
                      isSearchable
                      options={COUNTRIES}
                      defaultValue={COUNTRIES.find((val) => val.value === guest.nationality)}
                      onChange={async (
                        value: ValueType<{ value: string; label: string }, false>
                      ) => {
                        await updateOneGuest({ ...guest, nationality: value?.value });
                        window.location.reload();
                      }}
                      styles={selectStyles}
                    />
                  ) : (
                    <span>{guest.nationality || '—'}</span>
                  )}
                </Field>
                <Field label="Purpose of stay">
                  {canModify ? (
                    <Select
                      isSearchable
                      options={GUEST_VISIT_TYPES}
                      defaultValue={GUEST_VISIT_TYPES.find(
                        (val) => val.value === guest.purposeOfStay
                      )}
                      onChange={async (
                        value: ValueType<{ value: string; label: string }, false>
                      ) => {
                        await updateOneGuest({ ...guest, purposeOfStay: value?.value });
                        window.location.reload();
                      }}
                      styles={selectStyles}
                    />
                  ) : (
                    <span>{guest.purposeOfStay || '—'}</span>
                  )}
                </Field>
              </div>

              {canSeeDocuments && (
                <div className="mt-[28px] grid grid-cols-1 lg:grid-cols-3 gap-[16px]">
                  {DOCUMENT_GROUPS.map((group) => {
                    const documents = (guest.document || []).filter((doc) =>
                      group.matches(doc.type)
                    );
                    return (
                      <div
                        key={group.type}
                        className="rounded-[12px] border border-gray-200 p-[16px]"
                      >
                        <div className="text-[14px] font-gilroy-medium text-neutral-500">
                          {group.title}
                        </div>
                        <div className="mt-[10px] space-y-[8px]">
                          {documents.map((doc, index) => (
                            <a
                              key={doc.documentId || index}
                              href={doc.documentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-[10px] rounded-[10px] bg-creams-100 px-[12px] py-[10px] text-neutral-500 no-underline hover:no-underline hover:bg-creams-200 hover:text-neutral-500"
                            >
                              <HugeiconsIcon icon={File01Icon} size={18} className="shrink-0" />
                              <span className="min-w-0">
                                <span className="block text-[14px] truncate">
                                  {group.title} {index + 1}
                                </span>
                                <span className="block text-[12px] text-neutral-300 truncate">
                                  {doc.documentId}
                                </span>
                              </span>
                            </a>
                          ))}
                          <Dropzone
                            classNames={{
                              dropzone:
                                '!relative !flex !items-center !justify-center !w-full !min-h-[64px] !overflow-hidden !border !border-dashed !border-gray-200 !rounded-[10px] !bg-neutral-0 hover:!border-neutral-300',
                              input: '!hidden'
                            }}
                            inputContent={() => (
                              <div
                                key="input"
                                className="flex items-center justify-center gap-[8px] text-[13px] text-neutral-300"
                              >
                                {uploadingType === group.type ? (
                                  <img src={`${PUBLIC_PATH}/assets/svgs/loader.svg`} alt="loader" />
                                ) : (
                                  <>
                                    <HugeiconsIcon icon={CloudUploadIcon} size={18} />
                                    {group.upload}
                                  </>
                                )}
                              </div>
                            )}
                            accept="*"
                            maxFiles={1}
                            multiple={false}
                            onSubmit={(files) => {
                              setUploadingType(group.type);
                              files.forEach((file) => {
                                handleUploadDocument(
                                  file.file,
                                  guest.id || '',
                                  reservation.id,
                                  group.type
                                );
                                file.remove();
                              });
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-[24px]">
                <button
                  type="button"
                  disabled={isLoadingDeleteGuest}
                  onClick={() => {
                    deleteOneGuest(guest.id || '').then(() =>
                      birdToast.success(`successfully delete ${guest.firstName}.`)
                    );
                  }}
                  className="inline-flex items-center gap-[6px] px-[14px] py-[8px] rounded-[8px] text-[14px] font-gilroy-medium text-scarlet hover:bg-secondary-50 disabled:opacity-60 cursor-pointer"
                >
                  <HugeiconsIcon icon={Delete02Icon} size={16} />
                  {isLoadingDeleteGuest ? 'Deleting…' : 'Delete guest'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};
